<template>
  <div v-if="config">
    <CardTabs
      ref="cardTabs"
      :config="config"
      :contract-type-status="contractTypeStatus"
      :contract="currentContract"
      :module-field="moduleField"
      :contract-modules="contractModules"
      :object-type="objectType"
      @change="handleChangeContractTypeStatus"
    />
    <el-card
      class="card-container"
      shadow="never"
    >
      <CardHeader
        slot="header"
        :config="config"
        :total="total"
        :contract="currentContract"
        :current-contract-status="currentContractStatus"
        :filter-params="params"
        @create="handleCreate"
        @refresh="handleRefresh"
      />
      <CardFilter
        :config="config"
        :contract-modules="contractModules"
        @search="handleSearch"
        @advance-search="filterVisible = true"
      />
      <CardList
        :config="config"
        :data="data"
        :approved-data="approvedData"
        :avatar-data="avatarData"
        :loading="loading"
        :contract="currentContract"
        :contract-type-status="contractTypeStatus"
        @show="handleShow"
        @history="handleHistory"
        @request="handleRequest"
      />
      <el-row
        type="flex"
        align="middle"
        justify="end"
        class="mt-l"
      >
        <el-pagination
          background
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 500, 1000]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleChangePageSize"
          @current-change="handleChangePage"
        />
      </el-row>
      <FilterFormDrawer
        v-if="filterVisible"
        :config="config"
        :visible="filterVisible"
        :filter-form="advanceFilter"
        @close="filterVisible = false"
        @search="listData"
      />
      <RequestFormPopup
        v-if="requestVisible"
        :config="config"
        :object-type="objectType"
        :item="selectedItem"
        :contract="currentContract"
        :action="selectedAction"
        :visible="requestVisible"
        @close="requestVisible = false"
        @reload="reloadListData"
      />
      <ApprovedHistoryPopup
        v-if="historyVisible"
        :show-fast="showFast"
        :visible="historyVisible"
        :item="selectedItem"
        :contract="currentContract"
        @close="historyVisible = false"
      />
    </el-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import CardHeader from "./components/CardHeader.vue";
import CardTabs from "./components/CardTabs.vue";
import CardFilter from "./components/CardFilter.vue";
import CardList from "./components/CardList.vue";
import FilterFormDrawer from "./components/AdvancedFilter.vue";

import RequestFormPopup from "./popups/RequestForm.vue";
import ApprovedHistoryPopup from "./popups/ApprovedHistory.vue";

import commonServices from "@/services/common";
import organizationServices from "@/services/organization";
import approvedServices from "@/services/approved";
import fileServices from "@/services/file";
import constants from "@/config/constants";

export default {
  name: "ApprovedDocuments",
  components: {
    CardHeader,
    CardTabs,
    CardFilter,
    CardList,
    FilterFormDrawer,
    RequestFormPopup,
    ApprovedHistoryPopup
  },
  data() {
    return {
      fileType: constants.FIELD_TYPE,
      loading: false,
      page: 1,
      pageSize: constants.PAGE_SIZE,
      total: 0,
      data: [],
      approvedData: [],
      avatarData: [],
      selectedItem: null,
      filterVisible: false,
      searchText: "",
      advanceFilter: {},
      organizationId: organizationServices.getCurrentOrg()?.id,
      contractTypeStatus: 'none',
      selectedAction: null,
      requestVisible: false,
      historyVisible: false,
      showFast: false,
      fastFilter: {},
    };
  },
  computed: {
    ...mapGetters("config", ["allConfigs"]),
    ...mapGetters("category", ["allCategories"]),
    ...mapGetters("contract", ["allContracts", "allContractStatus"]),
    config() {
      return this.allConfigs.find((c) => c.extra.key === "artifacts");
    },
    objectType() {
      return constants.OBJECT_TYPES.find((t) => t.value === this.config.type);
    },
    contractType() {
      return this.$route.query.contract_type || this.allContracts[0]?.contract_type
    },
    currentContract() {
      if (this.contractType) {
        return this.allContracts.find((c) => c.contract_type === this.contractType)
      }
      return null
    },
    currentContractStatus() {
      if (this.contractTypeStatus !== 'none') {
        return this.allContractStatus.find((s) => s.value === this.contractTypeStatus)
      }
      return null
    },
    contractModules() {
      const modules = this.config.extra.modules || [];
      return modules.filter((m) => m.contracts.includes(this.currentContract?.id))
    },
    moduleField() {
      return this.config.extra?.fields?.find((f) => f.type === this.fileType.MODULE)
    },
    params() {
      const _params = {
        offset: (this.page - 1) * this.pageSize,
        limit: this.page * this.pageSize,
        locale: "vi",
        sort: "-created"
      };
      _params.filters = {
        type: this.config.extra.key,
        organization_id: this.organizationId,
      };
      if (this.searchText) {
        _params.filters[`extra.name`] = { $regex: this.searchText }
      }
      if (this.contractType) {
        _params.filters[`contract_status.${this.contractType}`] = this.contractTypeStatus === 'none' ? null : this.contractTypeStatus || null
      }
      if (this.moduleField && this.contractModules.length > 0) {
        _params.filters[`extra.${this.moduleField.key}`] = { $in: this.contractModules.map((m) => m.key) }
      }
      Object.keys(this.fastFilter).forEach((fastFilterKey) => {
        if (this.fastFilter[fastFilterKey]) {
          _params.filters[`extra.${fastFilterKey}`] = this.fastFilter[fastFilterKey]
        }
      })
      Object.keys(this.advanceFilter).forEach((filterKey) => {
        if (this.advanceFilter[filterKey]) {
          const field = this.config?.extra?.fields?.find(
            (f) => f.key === filterKey
          );
          if (field) {
            if ([this.fileType.NUMBER, this.fileType.SELECT, this.fileType.RATIO].includes(field.type)) {
              _params.filters[`extra.${filterKey}`] = this.advanceFilter[filterKey];
            } else if ([this.fileType.CHECKBOX, this.fileType.MULTIPLE_SELECT].includes(field.type)) {
              _params.filters[`extra.${filterKey}`] = {
                $in: this.advanceFilter[filterKey]
              };
            } else {
              _params.filters[`extra.${filterKey}`] = {
                $regex: this.advanceFilter[filterKey],
              };
            }
          }
        }
      });
      return _params;
    },
    approvedParams() {
      const _params = {
        offset: (this.page - 1) * this.pageSize,
        limit: this.page * this.pageSize,
        locale: "vi",
      }
      _params.filters = {
        document_type: this.objectType.role_action,
        organization_id: this.organizationId,
        document_id: { $in: this.data.map((d) => d.id) },
      };
      if (this.currentContract) {
        _params.filters['contract_id'] = this.currentContract.id
      }
      return _params
    },
    fileParams() {
      const fileIds = this.data.map((d) => {
        if (d.extra.file_images?.length > 0) {
          return d.extra.file_images[0].id
        }
        return null
      }).filter(Boolean)
      const _params = {
        offset: 0,
        limit: fileIds.length,
        locale: "vi",
      };
      _params.filters = {
        id: { $in: fileIds },
        organization_id: this.organizationId,
      };
      return _params;
    }
  },
  watch: {
    config: "listData",
    contractType() {
      this.contractTypeStatus = 'none';
      this.listData();
    },
  },
  beforeMount() {
    this.listData();
  },
  methods: {
    listData() {
      if (!this.config) {
        return;
      }
      this.loading = true;
      this.selectedData = [];
      commonServices
        .list(this.config.type, this.params)
        .then(async (response) => {
          this.data = response.items.map((i, index) => ({
            ...i,
            stt: index + 1 + this.pageSize * (this.page - 1),
          }));
          this.total = response.meta.total;
          await this.listApprovedData();
          await this.listFilesData();
          this.loading = false;
        })
        .catch(() => {
          this.data = [];
          this.loading = false;
        });
    },
    async listApprovedData() {
      approvedServices.list(this.approvedParams).then((response) => {
        this.approvedData = response.items;
      }).catch(() => {
        this.approvedData = []
      })
    },
    async listFilesData() {
      fileServices.list(this.fileParams).then((response) => {
        this.avatarData = response.items;
      }).catch(() => {
        this.avatarData = []
      })
    },
    handleCreate() {
      this.selectedItem = null;
      this.formVisible = true;
    },
    handleTag(item) {
      this.selectedItem = item;
      this.tagVisible = true;
    },
    handleUpdate(item) {
      const routeData = this.$router.resolve({
        name: `${this.config.extra.key}_show`,
        params: { id: item.id },
      })
      window.open(routeData.href, '_blank');
    },
    handleView(item) {
      const routeData = this.$router.resolve({
        name: `${this.config.extra.key}_view`,
        params: {
          id: item.id
        }
      })
      window.open(routeData.href, '_blank');
    },
    handleConnection(item) {
      const routeData = this.$router.resolve({
        name: `${this.config.extra.key}_connection`,
        params: { id: item.id },
      })
      window.open(routeData.href, '_blank');
    },
    handleSearch(payload) {
      this.page = 1;
      this.searchText = payload.searchText;
      this.fastFilter = payload.fastFilter;
      this.listData();
    },
    handleRefresh() {
      this.selectedItem = null;
      this.page = 1;
      this.searchText = "";
      this.advanceFilter = {};
      this.listData();
    },
    handleChangePage(page) {
      this.page = page;
      this.listData();
    },
    handleChangePageSize(size) {
      this.page = 1;
      this.pageSize = size;
      this.listData();
    },
    handleChangeContractTypeStatus(value) {
      this.contractTypeStatus = value;
      this.listData();
    },
    handleRequest(item, action) {
      this.selectedAction = action;
      this.selectedItem = item;
      this.requestVisible = true;
    },
    handleHistory(item) {
      this.showFast = false;
      this.selectedItem = item;
      this.historyVisible = true;
    },
    handleShow(item) {
      this.showFast = true;
      this.selectedItem = item;
      this.historyVisible = true;
    },
    reloadListData() {
      this.listData();
      this.$refs.cardTabs.getObjectCountData();
    }
  },
};
</script>

<style lang="scss" scoped></style>
